import pxToRem from "../functions/pxToRem";
import { DISPLAY_TRACKING, DISPLAY_WEIGHT, FONT_BODY, FONT_DISPLAY } from "../tokens/fonts";

// The reference body never goes below 400, so "light" text renders at the regular weight.
const baseProperties = {
  fontFamily: FONT_BODY,
  fontWeightLighter: 300,
  fontWeightLight: 400,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  fontSizeXXS: pxToRem(10.4),
  fontSizeXS: pxToRem(12),
  fontSizeSM: pxToRem(14),
  fontSizeMD: pxToRem(16),
  fontSizeLG: pxToRem(18),
  fontSizeXL: pxToRem(20),
  fontSize2XL: pxToRem(24),
  fontSize3XL: pxToRem(30),
};

// Headings take the theme text colour, so they turn light in dark mode.
export default function typography({ colors }) {
  const baseHeadingProperties = {
    fontFamily: FONT_DISPLAY,
    color: colors.text.primary,
    fontWeight: DISPLAY_WEIGHT,
    letterSpacing: DISPLAY_TRACKING,
  };

  const baseDisplayProperties = {
    fontFamily: FONT_DISPLAY,
    color: colors.text.primary,
    fontWeight: DISPLAY_WEIGHT,
    letterSpacing: DISPLAY_TRACKING,
    lineHeight: 1.2,
  };

  const typography = {
    fontFamily: baseProperties.fontFamily,
    fontWeightLighter: baseProperties.fontWeightLighter,
    fontWeightLight: baseProperties.fontWeightLight,
    fontWeightRegular: baseProperties.fontWeightRegular,
    fontWeightMedium: baseProperties.fontWeightMedium,
    fontWeightBold: baseProperties.fontWeightBold,

    h1: {
      fontSize: pxToRem(48),
      lineHeight: 1.25,
      ...baseHeadingProperties,
    },

    h2: {
      fontSize: pxToRem(36),
      lineHeight: 1.3,
      ...baseHeadingProperties,
    },

    h3: {
      fontSize: pxToRem(30),
      lineHeight: 1.375,
      ...baseHeadingProperties,
    },

    h4: {
      fontSize: pxToRem(24),
      lineHeight: 1.375,
      ...baseHeadingProperties,
    },

    h5: {
      fontSize: pxToRem(20),
      lineHeight: 1.375,
      ...baseHeadingProperties,
    },

    h6: {
      fontSize: pxToRem(16),
      lineHeight: 1.625,
      ...baseHeadingProperties,
    },

    subtitle1: {
      fontFamily: baseProperties.fontFamily,
      fontSize: baseProperties.fontSizeXL,
      fontWeight: baseProperties.fontWeightLight,
      lineHeight: 1.625,
    },

    subtitle2: {
      fontFamily: baseProperties.fontFamily,
      fontSize: baseProperties.fontSizeMD,
      fontWeight: baseProperties.fontWeightLight,
      lineHeight: 1.6,
    },

    body1: {
      fontFamily: baseProperties.fontFamily,
      fontSize: baseProperties.fontSizeXL,
      fontWeight: baseProperties.fontWeightRegular,
      lineHeight: 1.625,
    },

    body2: {
      fontFamily: baseProperties.fontFamily,
      fontSize: baseProperties.fontSizeMD,
      fontWeight: baseProperties.fontWeightLight,
      lineHeight: 1.6,
    },

    button: {
      fontFamily: baseProperties.fontFamily,
      fontSize: baseProperties.fontSizeSM,
      fontWeight: baseProperties.fontWeightLight,
      lineHeight: 1.5,
      textTransform: "uppercase",
    },

    caption: {
      fontFamily: baseProperties.fontFamily,
      fontSize: baseProperties.fontSizeXS,
      fontWeight: baseProperties.fontWeightLight,
      lineHeight: 1.25,
    },

    overline: {
      fontFamily: baseProperties.fontFamily,
    },

    d1: {
      fontSize: pxToRem(80),
      ...baseDisplayProperties,
    },

    d2: {
      fontSize: pxToRem(72),
      ...baseDisplayProperties,
    },

    d3: {
      fontSize: pxToRem(64),
      ...baseDisplayProperties,
    },

    d4: {
      fontSize: pxToRem(56),
      ...baseDisplayProperties,
    },

    d5: {
      fontSize: pxToRem(48),
      ...baseDisplayProperties,
    },

    d6: {
      fontSize: pxToRem(40),
      ...baseDisplayProperties,
    },

    size: {
      xxs: baseProperties.fontSizeXXS,
      xs: baseProperties.fontSizeXS,
      sm: baseProperties.fontSizeSM,
      md: baseProperties.fontSizeMD,
      lg: baseProperties.fontSizeLG,
      xl: baseProperties.fontSizeXL,
      "2xl": baseProperties.fontSize2XL,
      "3xl": baseProperties.fontSize3XL,
    },

    lineHeight: {
      sm: 1.25,
      md: 1.5,
      lg: 2,
    },
  };

  return typography;
}
